/**
 * Thinccc Design System - Modal Component
 *
 * Dialog/Modal component matching Figma design
 */
import React, { useCallback, useEffect } from "react";

import { createPortal } from "react-dom";

import PropTypes from "prop-types";

// ===========================================
// MODAL COMPONENT
// ===========================================
const Modal = ({
  isOpen,
  onClose,
  children,
  size = "md",
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = "",
  ...props
}) => {
  // Handle escape key press
  const handleKeyDown = useCallback(
    (e) => {
      if (closeOnEscape && e.key === "Escape") {
        onClose?.();
      }
    },
    [closeOnEscape, onClose],
  );

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose?.();
    }
  };

  // Add event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  // Size styles
  const sizeStyles = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-[calc(100vw-2rem)]",
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`
        fixed inset-0 z-[1400]
        flex items-center justify-center
        p-4
        animate-fade-in
      `
        .replace(/\s+/g, " ")
        .trim()}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      {...props}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal content */}
      <div
        className={`
          relative
          w-full
          ${sizeStyles[size]}
          bg-background-midnight
          rounded-3xl
          shadow-modal
          animate-scale-in
          ${className}
        `
          .replace(/\s+/g, " ")
          .trim()}
      >
        {/* Close button */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className={`
              absolute top-4 right-4
              w-10 h-10
              flex items-center justify-center
              rounded-full
              text-text-lv3
              hover:bg-white/8 hover:text-text-lv1
              transition-colors duration-fast
            `
              .replace(/\s+/g, " ")
              .trim()}
            aria-label="Close modal"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {children}
      </div>
    </div>,
    document.body,
  );
};

Modal.displayName = "Modal";

// ===========================================
// MODAL SUB-COMPONENTS
// ===========================================

// Modal Header
const ModalHeader = ({ children, className = "", ...props }) => (
  <div className={`px-6 pt-6 pb-4 ${className}`} {...props}>
    {children}
  </div>
);

ModalHeader.displayName = "ModalHeader";

// Modal Title
const ModalTitle = ({ children, className = "", ...props }) => (
  <h2
    className={`text-xl font-medium text-text-lv0 pr-8 ${className}`}
    {...props}
  >
    {children}
  </h2>
);

ModalTitle.displayName = "ModalTitle";

// Modal Description
const ModalDescription = ({ children, className = "", ...props }) => (
  <p className={`mt-2 text-base text-text-lv3 ${className}`} {...props}>
    {children}
  </p>
);

ModalDescription.displayName = "ModalDescription";

// Modal Body
const ModalBody = ({ children, className = "", ...props }) => (
  <div className={`px-6 py-4 ${className}`} {...props}>
    {children}
  </div>
);

ModalBody.displayName = "ModalBody";

// Modal Footer
const ModalFooter = ({ children, className = "", ...props }) => (
  <div
    className={`
      flex items-center justify-end gap-3
      px-6 py-4
      border-t border-background-biscay
      ${className}
    `
      .replace(/\s+/g, " ")
      .trim()}
    {...props}
  >
    {children}
  </div>
);

ModalFooter.displayName = "ModalFooter";

// ===========================================
// PROP TYPES
// ===========================================
Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  children: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl", "full"]),
  closeOnBackdrop: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
};

ModalHeader.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ModalTitle.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ModalDescription.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ModalBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ModalFooter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

// Attach sub-components
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Description = ModalDescription;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
